/**
 * Las líneas de la cabecera, compuestas a partir de los datos que la empresa
 * ENSEÑA (29-09-2026). Puro: lo usan el cargador (con lo que da la RPC, que ya
 * solo trae lo visible), la página «Personalizar informes» (con el borrador) y
 * los tests.
 *
 *   «Frutas Ejemplo S.L. · NIF B00000000 · C/ Ejemplo 1, 04117 Níjar (Almería)»
 *   «Tel. 950 000 000 · info@ejemplo.com · www.ejemplo.com»
 *   «<la línea libre>»
 */

/** Las claves que la empresa puede encender en la cabecera (mismo orden que la tabla). */
export const CAMPOS_VISIBLES = ["razon_social", "nif", "direccion", "telefono", "email", "web", "linea_extra"] as const;
export type CampoVisible = (typeof CAMPOS_VISIBLES)[number];

export interface CamposCabecera {
  razon_social?: string | null;
  nif?: string | null;
  direccion?: string | null;
  codigo_postal?: string | null;
  localidad?: string | null;
  provincia?: string | null;
  telefono?: string | null;
  email?: string | null;
  web?: string | null;
  linea_extra?: string | null;
}

export interface EtiquetasCabecera {
  /** «NIF» */
  nif: string;
  /** «Tel.» */
  tel: string;
}

const limpio = (v: string | null | undefined) => (v ?? "").replace(/\s+/g, " ").trim();

/** «C/ Ejemplo 1, 04117 Níjar (Almería)»: lo que haya, sin comas ni paréntesis sueltos. */
export function direccionCompleta(c: CamposCabecera): string {
  const lugar = [limpio(c.codigo_postal), limpio(c.localidad)].filter(Boolean).join(" ");
  const prov = limpio(c.provincia);
  const lugarProv = prov ? (lugar ? `${lugar} (${prov})` : prov) : lugar;
  return [limpio(c.direccion), lugarProv].filter(Boolean).join(", ");
}

/**
 * Las líneas con lo que venga RELLENO. Quien llama decide qué entra (la RPC ya
 * solo trae lo visible; la página filtra el borrador con `soloVisibles`). La razón
 * social no se repite si es el mismo nombre que encabeza.
 */
export function componerLineas(c: CamposCabecera, et: EtiquetasCabecera, nombre = ""): string[] {
  const razon = limpio(c.razon_social);
  const mismaQueNombre = razon && razon.toLowerCase() === limpio(nombre).toLowerCase();
  const nif = limpio(c.nif);
  const tel = limpio(c.telefono);
  const l1 = [mismaQueNombre ? "" : razon, nif ? `${et.nif} ${nif}` : "", direccionCompleta(c)].filter(Boolean).join(" · ");
  const l2 = [tel ? `${et.tel} ${tel}` : "", limpio(c.email), limpio(c.web)].filter(Boolean).join(" · ");
  return [l1, l2, limpio(c.linea_extra)].filter(Boolean);
}

/** Los campos del borrador, con lo que NO está encendido en blanco (la dirección arrastra CP, localidad y provincia). */
export function soloVisibles(c: CamposCabecera, visibles: readonly string[]): CamposCabecera {
  const ve = new Set(visibles);
  return {
    razon_social: ve.has("razon_social") ? c.razon_social : null,
    nif: ve.has("nif") ? c.nif : null,
    direccion: ve.has("direccion") ? c.direccion : null,
    codigo_postal: ve.has("direccion") ? c.codigo_postal : null,
    localidad: ve.has("direccion") ? c.localidad : null,
    provincia: ve.has("direccion") ? c.provincia : null,
    telefono: ve.has("telefono") ? c.telefono : null,
    email: ve.has("email") ? c.email : null,
    web: ve.has("web") ? c.web : null,
    linea_extra: ve.has("linea_extra") ? c.linea_extra : null,
  };
}
